"use client";

import * as React from "react";
import Image from "next/image";
import Underline from "@/components/ui/underline";
import CtaButton from "@/components/ui/CtaButton";
import { LazyMotion, domAnimation, m, type HTMLMotionProps } from "framer-motion";

export type AboutMissionClientProps = HTMLMotionProps<"section"> & {
    tagline: string;
    heading: string;
    paragraphs: string[];
    images: { src: string; blur?: string; sizes: string; alt?: string }[];
};

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.8,
            ease: [0.215, 0.61, 0.355, 1],
            delay: i * 0.1,
        },
    }),
} as const;

const reveal = {
    hidden: { clipPath: "inset(100% 0% 0% 0%)" },
    visible: (i: number) => ({
        clipPath: "inset(0% 0% 0% 0%)",
        transition: {
            duration: 1.2,
            ease: [0.45, 0, 0.55, 1],
            delay: 0.2 + i * 0.15,
        },
    }),
} as const;

// ✅ POPRAWKA:
// Tablet (md): Usuwamy sztywne szerokości (np. md:w-[200px]). Grid sam ustawi szerokość (flex-1).
// Desktop (lg): Przywracamy sztywne szerokości dla artystycznego układu.
function aspectClass(index: number) {
    if (index === 0) return "aspect-[4/5] lg:w-[240px]";
    if (index === 1) return "aspect-[2/3] lg:w-[300px]";
    return "aspect-[2/3] lg:w-[420px]";
}

function maxWClass(index: number) {
    // Na mobile ograniczamy, na tablecie/desktopie puszczamy wolno (grid/flex trzyma ramy)
    if (index === 0) return "max-w-[260px] md:max-w-none";
    if (index === 1) return "max-w-[300px] md:max-w-none";
    return "max-w-[360px] md:max-w-none";
}

export default function AboutMissionClient({
                                               tagline,
                                               heading,
                                               paragraphs,
                                               images,
                                               className,
                                               ...rest
                                           }: AboutMissionClientProps) {
    return (
        <LazyMotion features={domAnimation}>
            <m.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className={`px-[5%] py-16 md:py-24 lg:py-28 overflow-hidden ${className ?? ""}`}
                {...rest}
            >
                <div className="container">
                    {/* GRID GŁÓWNY: Tekst + Pusta przestrzeń na desktopie */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                        <div className="flex flex-col items-start relative z-10 max-w-none lg:max-w-3xl">
                            <m.div variants={slideUp} custom={0} className="relative inline-block mb-8">
                                <p className="uppercase tracking-[0.10em] text-sm">{tagline}</p>
                                <Underline className="pointer-events-none absolute -left-2 top-full -mt-1 text-accent h-4 w-[150px] md:h-6 md:w-[180px]" />
                            </m.div>

                            <m.h2 variants={slideUp} custom={1} className="text-3xl font-medium leading-[1.1] md:text-5xl mb-5 md:min-w-[800px]">
                                {heading}
                            </m.h2>

                            {/* Ograniczenie szerokości tekstu na tablecie dla czytelności */}
                            <div className="space-y-6 mb-10 max-w-[56ch] md:max-w-[70ch]">
                                {paragraphs.map((p, idx) => (
                                    <m.p
                                        key={idx}
                                        variants={slideUp}
                                        custom={idx + 2}
                                        className="text-base leading-relaxed md:text-md opacity-80"
                                    >
                                        {p}
                                    </m.p>
                                ))}
                            </div>

                            <m.div variants={slideUp} custom={paragraphs.length + 2}>
                                <CtaButton />
                            </m.div>
                        </div>

                        <div className="hidden lg:block" />
                    </div>

                    {/* ✅ UKŁAD ZDJĘĆ - FIX
                        1. Usunięto nowe linie w className (naprawa błędu Hydration).
                        2. Mobile: grid-cols-1 (jeden pod drugim).
                        3. Tablet (md): grid-cols-3 (trzy obok siebie, równe).
                        4. Desktop (lg): flex (powrót do artystycznego układu z różnymi szerokościami).
                    */}
                    <div className="md:px-[5%] mt-12 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-4 md:items-end lg:flex lg:flex-row lg:justify-end lg:gap-8 lg:mt-[-10rem] relative z-0">
                        {images.slice(0, 3).map((img, index) => {
                            const aClass = aspectClass(index);
                            const wClass = maxWClass(index);

                            return (
                                <div key={img.src} className={`w-full ${wClass} md:w-auto`}>
                                    <m.div
                                        variants={reveal}
                                        custom={index}
                                        className={`relative ${aClass} w-full overflow-hidden rounded-2xl shadow-xl`}
                                    >
                                        <Image
                                            src={img.src}
                                            alt={img.alt ?? ""}
                                            fill
                                            sizes={img.sizes}
                                            className="object-cover transition-transform duration-700 mouse:hover:scale-105"
                                            placeholder={img.blur ? "blur" : "empty"}
                                            blurDataURL={img.blur || undefined}
                                            decoding="async"
                                            draggable={false}
                                        />
                                    </m.div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}